import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, InputBase, makeStyles, TextField } from '@material-ui/core';
import { useSelector } from 'react-redux';
import _, { debounce, isArray } from 'lodash';
import { AutoCompleteSelect } from '../../../../components';
import AutoCompleteInput from '../../../../components/AutoCompleteInput';
import DiaLog from '../../../../components/Dialog';
import { DropDownIcon, DropUpIcon, ScanIcon } from '../../../../components/Icon';
import StyleButton from '../../../../components/StyleButton';
import { equipmentAPI } from '../../../../apis';
import { i18n } from '../../../../language';
import { EQUIPMENT_LENGTH, EQUIPMENT_STATUS_ENUM, REMARKS_FIELD } from '../../../../comm/constant';
import { useScanPage } from '../../../../utils/useScanQrcode';
import useMobile from '../../../../utils/useMobile';
import useUpdateEffect from '../../../../utils/useUpdateEffect';
import {
  DWP_COMM_REMARK_BMS_ISFA,
  DWP_COMM_REMARK_BMS_L,
  DWP_COMM_REMARK_BMS_M,
  DWP_COMM_REMARK_BMS_S,
} from '../../../../comm/commandQRcode';
import useBarcodeListener from '../../../../utils/useBarcodeListener';
import useGetRemarksListAPI from '../../hooks/useGetRemarksListAPI';
// import AddRemarks from '../../../../components/AddRemarks';
import { onKeyUpEnterOrSpace } from '../../../../utils';

const useStyles = makeStyles(() => ({
  textTitle: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#7d86a9',
    marginBottom: '0.25rem',
  },
  inputTitle: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#7d86a9',
    marginBottom: '1rem',
  },
  assetId: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1.313rem',
    color: '#1973e6',
  },
  statusIcon: {
    width: '0.625rem',
    height: '0.625rem',
    minWidth: '0.625rem',
    minHeight: '0.625rem',
    borderRadius: '50%',
    marginRight: '0.625rem',
  },
  statusText: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  },
  otherInfomation: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.125rem',
    color: '#3e3e3e',
    display: 'flex',
    padding: '1.063rem 0.719rem',
    borderTop: 'solid 1px #e6e7e8',
    borderBottom: 'solid 1px #e6e7e8',
    cursor: 'pointer',
  },
  dropDownButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '1.5rem',
    height: '1.5rem',
    marginRight: '1.25rem',
  },
  dropDownIcon: {
    fontSize: '0.938rem',
    color: '#3e3e3e',
  },
  resetIcon: {
    fontSize: '1.1rem',
    color: '#538FFF',
    marginRight: '0.5rem',
  },
  inputBase: ({ error }) => ({
    border: error ? 'solid 1px #f44336' : 'solid 1px #e4e7eb',
    borderRadius: '10px',
    padding: '0.7rem 1.25rem',
    fontFamily: 'NotoSans-Medium',
    fontSize: '1rem',
    color: '#2e4469',
  }),
  errorMsg: {
    color: '#f44336',
    margin: '0.5rem 0.5rem 0',
    fontFamily: 'NotoSans-Medium',
    fontWeight: '500',
  },
  star: {
    color: '#ff0300',
  },
  scanButton: {
    marginBottom: '0.25rem',
    flexBasis: '66%',
  },
  textField: {
    borderRadius: '0.625rem',
    border: 'solid 1px #e4e7eb',
    padding: '0 1rem',
    '& .MuiInputBase-root': {
      fontFamily: 'NotoSans-Medium',
      fontSize: '1rem',
      color: '#2e4469',
      lineHeight: '1.8rem',
    },
    '& :before': {
      content: 'none',
    },
    '& :after': {
      content: 'none',
    },
  },
}));

const InputEquipment = ({
  value,
  onChange,
  placeholder,
  style,
  maxLength,
  check,
  error,
  errorMsg,
  disabled = false,
  id,
}) => {
  const classes = useStyles({ error });
  const [inputValue, setInputValue] = useState('');

  // 防抖函數
  const debouncedSearch = useRef(
    debounce(async (props) => {
      onChange(props);
      check(props);
    }, 1000)
  ).current;

  // 清除副作用
  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  // input 的 onChange 事件
  const changeHandler = useCallback(
    (event) => {
      setInputValue(event.target.value);
      check ? debouncedSearch(event.target.value) : onChange(event.target.value);
    },
    [onChange, debounce]
  );

  return (
    <>
      <InputBase
        id={id}
        className={classes.inputBase}
        value={check ? inputValue : value}
        onChange={changeHandler}
        placeholder={placeholder}
        style={style}
        inputProps={{ maxLength }}
        disabled={disabled}
      />
      {error && <div className={classes.errorMsg}>{errorMsg}</div>}
    </>
  );
};

const replaceStrBmss = DWP_COMM_REMARK_BMS_S;
const replaceStrBmsm = DWP_COMM_REMARK_BMS_M;
const replaceStrBmsl = DWP_COMM_REMARK_BMS_L;
const replaceStrBmsIsfa = DWP_COMM_REMARK_BMS_ISFA;

const replaceHandler = (oldReplaceValue, newReplaceValue, value, enter, temp) => {
  const reg = new RegExp(oldReplaceValue, 'g');
  let str;
  if (value.target) {
    str = value.target.value.replace(reg, newReplaceValue);
  } else if (enter) {
    str = `${temp.remarks + value.replace(reg, newReplaceValue)}\n`;
  } else {
    str = `${temp.remarks + value.replace(reg, newReplaceValue)}`;
  }

  return str;
};

const judgeConstantRemark = (val, constantRemark) => val.indexOf(constantRemark) !== -1;

const handleInputRemark = ({ setFormData, value, enter }) => {
  setFormData((preData) => {
    let temp = { ...preData };
    let val = value.target ? value.target.value : value;

    if (judgeConstantRemark(val, replaceStrBmss)) {
      val = replaceHandler(replaceStrBmss, REMARKS_FIELD.BIPAP_MASK_SIZE_S, val, enter, temp);
    } else if (judgeConstantRemark(val, replaceStrBmsm)) {
      val = replaceHandler(replaceStrBmsm, REMARKS_FIELD.BIPAP_MASK_SIZE_M, val, enter, temp);
    } else if (judgeConstantRemark(val, replaceStrBmsl)) {
      val = replaceHandler(replaceStrBmsl, REMARKS_FIELD.BIPAP_MASK_SIZE_L, val, enter, temp);
    } else if (judgeConstantRemark(val, replaceStrBmsIsfa)) {
      val = replaceHandler(replaceStrBmsIsfa, REMARKS_FIELD.EXCEPT_ISOLATION_WARD, val, enter, temp);
    } else if (val.indexOf(REMARKS_FIELD.START_CODE) !== -1 && val.indexOf(REMARKS_FIELD.END_CODE) !== -1) {
      if (enter) {
        val = temp.remark + val.replace(REMARKS_FIELD.REPLACE_REMARK_REGEX, (value) => `${value.slice(13, -12)}\n`);
      } else {
        val = value.target
          ? val.replace(REMARKS_FIELD.REPLACE_REMARK_REGEX, (value) => value.slice(13, -12))
          : temp.remark + val.replace(REMARKS_FIELD.REPLACE_REMARK_REGEX, (value) => value.slice(13, -12));
      }
    }

    temp.remarks = val;
    return { ...temp };
  });
};

const RegisterEquipment = ({ closeHandler, successHandler }) => {
  const classes = useStyles();
  const inputRef = useRef(null);
  const [otherInformationOpen, setOtherInformationOpen] = useState(false);
  const loginInfo = useSelector((state) => state.loginInfo);
  const [providers, setProviders] = useState();
  const [categorys, setCategorys] = useState();
  const [status, setStatus] = useState();
  const [brand, setBrand] = useState();
  const [model, setModel] = useState();
  const [barcodeListener, setBarcodeListener] = useState(true);
  const [formData, setFormData] = useState({
    assetId: '',
    provider: loginInfo.wardId,
    category: '',
    status: 1,
    localCode: '',
    model: '',
    brand: '',
    remarks: '',
    reminderDuration: 14,
    categorySatelliteStoresId: 0,
  });
  const [errors, setErrors] = useState({
    localCode: '',
    assetId: '',
  });
  const [isPad] = useMobile();
  const [serviceTypeList, setServiceTypeList] = useState([]);
  const { remarkList: activeRemarkList, getRemarksListAPI } = useGetRemarksListAPI();

  // remark popup 选择
  const handleSelectRemark = (remark) => {
    setFormData((o = {}) => ({
      ...o,
      remarks: `${o?.remarks || ''} ${remark}`.slice(0, 99),
    }));
  };

  // 處理表單數據
  const formHandler = useCallback(
    (type, enter) => (value) => {
      if (type === 'remarks') {
        handleInputRemark({ setFormData, value, enter });
      } else {
        setFormData((preData) => ({ ...preData, [type]: value }));
      }
    },
    []
  );

  // 獲取 Provider 列表
  const getProvider = useCallback(async () => {
    try {
      const { result } = await equipmentAPI.getProvider({
        hospitalId: loginInfo.hospitalId,
        wardId: loginInfo.wardId,
      });
      setProviders(result.provides);
    } catch (error) {
      console.log(error);
    }
  }, [loginInfo]);

  // 獲取 Equipment Category 列表
  const getEquipmentCategory = useCallback(async () => {
    try {
      const { result } = await equipmentAPI.getEquipmentCategory({ hospitalId: loginInfo.hospitalId });
      setCategorys(result.categories);
    } catch (error) {
      console.log(error);
    }
  }, [loginInfo]);

  // 獲取 Status 列表
  const getStatus = useCallback(async () => {
    try {
      const { result } = await equipmentAPI.getRegisterStatus({
        wardId: loginInfo.wardId,
      });
      setStatus(result.status);
    } catch (error) {
      console.log(error);
    }
  }, [loginInfo]);

  // 獲取 brand && model list
  const getEquipmentModelAPI = useCallback(async () => {
    try {
      if (!formData.category) {
        return;
      }
      const { result } = await equipmentAPI.getEquipmentModel({ categoryId: formData.category });
      setBrand(result.brand);
      setModel(result.model);
    } catch (error) {
      console.log(error);
    }
  }, [formData.category]);

  useEffect(() => {
    (async () => {
      await getProvider();
    })();
    (async () => {
      await getEquipmentCategory();
    })();
    (async () => {
      await getStatus();
    })();
  }, [getEquipmentCategory, getStatus, getProvider]);

  useEffect(() => {
    (async () => {
      await getEquipmentModelAPI();
    })();
  }, [getEquipmentModelAPI]);

  const optionsMemo = useMemo(() => {
    let options = {
      provider: [{ text: loginInfo.wardName, value: loginInfo.wardId }],
      category: [],
      status: [],
      brand: [],
      model: [],
    };

    if (isArray(providers)) {
      options.provider = _.map(
        _.filter(providers, (item) => item.wardId === loginInfo.wardId),
        (item) => ({
          text: `${item.hospitalCode} - ${item.wardName}`,
          value: item.wardId,
          tagId: item.wardId,
        })
      );
    }

    if (isArray(categorys)) {
      options.category = _.map(categorys, (item) => ({
        text: item.categoryName,
        value: item.categoryId,
        tagId: item.categoryId,
      }));
    }

    if (isArray(status)) {
      options.status = _.map(status, (item) => ({
        text: i18n.t(
          _.find(EQUIPMENT_STATUS_ENUM, (statusItem) => statusItem.enumKey === item.id)?.textKey ??
            'EQUIPMENT_VIEWREQUEST_BLANK'
        ),
        value: item.id,
        tagId: item.id,
      }));
    }

    if (isArray(brand)) {
      options.brand = _.map(brand, (item) => ({
        text: item,
        value: item,
        tagId: item,
      }));
    }

    if (isArray(model)) {
      options.model = _.map(model, (item) => ({
        text: item,
        value: item,
        tagId: item,
      }));
    }

    return options;
  }, [categorys, status, brand, model, loginInfo, providers]);

  // other information 的開關
  const otherInformationController = useCallback(() => {
    setOtherInformationOpen((preCtrl) => !preCtrl);
  }, []);

  const submitHandler = useCallback(async () => {
    try {
      console.log('formData', formData);
      await equipmentAPI.registerEquipment({
        hospitalId: loginInfo.hospitalId,
        wardId: loginInfo.wardId,
        assetId: formData.assetId,
        provideWardId: formData.provider,
        categoryId: formData.category,
        status: formData.status,
        localCode: formData.localCode,
        model: formData.model,
        brand: formData.brand,
        remark: formData.remarks,
        reminderDuration: formData.reminderDuration,
        categorySatelliteStoresId: formData.categorySatelliteStoresId,
      });
      successHandler();
    } catch (error) {
      console.log(error);
    }
  }, [formData, successHandler, loginInfo]);

  // scan status qrcode
  const statusScanHandler = useCallback(
    (e) => {
      const handleChangeStatus = (number) => {
        if (_.some(optionsMemo.status, (item) => item.value === number)) {
          formHandler('status')(number);
        }
      };

      switch (e) {
        case 'DWP.COMM.EQ.STATUS.AVAILABLE':
          handleChangeStatus(1);
          break;
        case 'DWP.COMM.EQ.STATUS.STANDBY':
          handleChangeStatus(3);
          break;
        case 'DWP.COMM.EQ.STATUS.INUSE':
          handleChangeStatus(4);
          break;
        case 'DWP.COMM.EQ.STATUS.RETURNING':
          handleChangeStatus(5);
          break;
        case 'DWP.COMM.EQ.STATUS.NOTFORLOAN':
          handleChangeStatus(6);
          break;
        case 'DWP.COMM.EQ.STATUS.REPAIRING':
          handleChangeStatus(7);
          break;
        case 'DWP.COMM.EQ.STATUS.RESERVED':
          handleChangeStatus(8);
          break;
        case 'DWP.COMM.EQ.STATUS.CONDEMNED':
          handleChangeStatus(9);
          break;
        default:
          break;
      }
    },
    [optionsMemo.status]
  );

  // 監聽 barcode 槍 remarks
  useBarcodeListener((e) => {
    setOtherInformationOpen(true);
    inputRef.current.focus();
    formHandler('remarks')(e);
  }, barcodeListener);

  // check local code
  const checkLocalCode = useCallback(
    async (localCode) => {
      try {
        if (localCode) {
          const { result } = await equipmentAPI.checkLocalCode(
            {
              hospitalId: loginInfo.hospitalId,
              localcode: localCode,
            },
            { isHideLoading: true }
          );

          if (result === 'success') {
            setErrors((preErrors) => ({ ...preErrors, localCode: '' }));
            return;
          } else {
            setErrors((preErrors) => ({
              ...preErrors,
              localCode: i18n.t('EQUIPMENT_MANAGE_REGISTER_LOCAL_CODE_ERROR'),
            }));
          }
        } else {
          setErrors((preErrors) => ({ ...preErrors, localCode: '' }));
        }
      } catch (error) {
        console.log(error);
      }
    },
    [loginInfo, formData]
  );

  // Scan Equipment 的組件和調用函數,返回第一個是組件，第二個是開啟的函數
  const [ScanPortal, openScanPage] = useScanPage(formHandler('assetId'));
  const [ScanPortalStatus, openStatusScanPage] = useScanPage(statusScanHandler);
  const [ScanPortalRemarks, openScanPageRemarks] = useScanPage(formHandler('remarks', true));

  // confirm button disable
  const confirmDisable = useMemo(
    () =>
      !(
        formData.provider &&
        formData.assetId &&
        formData.category &&
        formData.status &&
        Boolean(!errors.localCode) &&
        Boolean(!errors.assetId)
      ),
    [formData.provider, formData.assetId, formData.category, formData.status, errors.localCode, errors.assetId]
  );

  // asset Id verify
  useUpdateEffect(() => {
    let onlyNumber = /^\d+$/;
    if (onlyNumber.test(formData.assetId)) {
      setErrors((preErrors) => ({ ...preErrors, assetId: '' }));
    } else {
      setErrors((preErrors) => ({ ...preErrors, assetId: i18n.t('EQUIPMENT_VIEWREQUEST_INVALID_ASSET_ERROR_MSG') }));
    }
  }, [formData.assetId]);

  // satellite store options
  const serviceTypeOptions = useMemo(() => {
    return serviceTypeList
      .filter((item) => item.hospitalCategoryId === formData.category)
      .map((item) => ({
        text: item.satelliteStoresName,
        value: item.categorySatelliteStoresId,
      }));
  }, [serviceTypeList, formData.category]);

  const getSatelliteStores = async () => {
    try {
      const { result } = await equipmentAPI.getSatelliteStores();
      setServiceTypeList(result);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    getSatelliteStores();
  }, []);

  useEffect(() => {
    if (formData?.category) {
      getRemarksListAPI({ hospitalCategoryIds: [formData?.category] });
    }
  }, [formData?.category]);

  return (
    <>
      <ScanPortal />
      <ScanPortalStatus />
      <ScanPortalRemarks />
      <DiaLog
        open={true}
        handleCancel={closeHandler}
        handleClose={closeHandler}
        handleConfirm={submitHandler}
        title={i18n.t('EQUIPMENT_MANAGE_REGISTER_TITLE')}
        cancel={i18n.t('EQUIPMENT_MANAGE_REGISTER_CANCEL')}
        confirm={i18n.t('EQUIPMENT_MANAGE_REGISTER_CONFIRM')}
        dialogfootStyle={{ marginTop: '1rem' }}
        disabled={confirmDisable}
      >
        <Box display="flex" justifyContent="space-between" alignItems="flex-end" gridColumnGap="1.5rem" mb="1.5rem">
          <Box display="flex" flexDirection="column" flexBasis="100%">
            <div className={classes.inputTitle}>
              {i18n.t('EQUIPMENT_MANAGE_REGISTER_ASSET_ID')} <span className={classes.star}>*</span>
            </div>
            <InputEquipment
              id="equipment-register-input-asset"
              className={classes.inputBase}
              value={formData.assetId}
              onChange={formHandler('assetId')}
              placeholder={i18n.t('EQUIPMENT_MANAGE_REGISTER_ASSET_ID_PLACEHOLDER')}
              style={{
                width: '100%',
                height: '3.5rem',
              }}
              maxLength={EQUIPMENT_LENGTH.EQUIPMENT_ASSET}
              error={Boolean(errors.assetId)}
              errorMsg={errors.assetId}
            />
          </Box>
          {isPad && (
            <StyleButton
              id="equipment-register-button-scan-equipment"
              className={classes.scanButton}
              width="14.125rem"
              text={i18n.t('EQUIPMENT_MANAGE_REGISTER_SCAN_EQUIPMENT')}
              Icon={ScanIcon}
              iconStyle={{ fontSize: '1.5rem', marginRight: '0.75rem' }}
              onClick={openScanPage}
            />
          )}
        </Box>
        <Box display="flex" flexDirection="column" justifyContent="space-between" gridColumnGap="1.5rem" mb="1.25rem">
          <div className={classes.inputTitle}>
            {i18n.t('EQUIPMENT_MANAGE_REGISTER_PROVIDER')} <span className={classes.star}>*</span>
          </div>
          <AutoCompleteSelect
            itemList={optionsMemo.provider}
            value={formData.provider}
            onChange={formHandler('provider')}
            disabled={true}
          />
        </Box>
        <Box display="flex" flexDirection="column" width="100%" mb="1.5rem">
          <div className={classes.inputTitle}>
            {i18n.t('EQUIPMENT_MANAGE_REGISTER_CATEGORY')} <span className={classes.star}>*</span>
          </div>
          <AutoCompleteSelect
            placeholder={i18n.t('EQUIPMENT_MANAGE_REGISTER_CATEGORY_PLACEHOLDER')}
            itemList={optionsMemo.category}
            value={formData.category}
            onChange={formHandler('category')}
          />
        </Box>
        {serviceTypeOptions?.length > 0 && (
          <Box display="flex" flexDirection="column" width="100%" mb="1.5rem">
            <div className={classes.inputTitle}>Satellite Store</div>
            <AutoCompleteSelect
              placeholder="Please select"
              itemList={[{ text: '', value: '' }, ...serviceTypeOptions]}
              value={formData.categorySatelliteStoresId}
              onChange={formHandler('categorySatelliteStoresId')}
            />
          </Box>
        )}
        <Box display="flex" justifyContent="space-between" alignItems="flex-end" gridColumnGap="1.438rem" mb="1.5rem">
          <Box display="flex" flexDirection="column" flexBasis="100%">
            <div className={classes.inputTitle}>
              {i18n.t('EQUIPMENT_MANAGE_REGISTER_STATUS')} <span className={classes.star}>*</span>
            </div>
            <AutoCompleteSelect
              placeholder={i18n.t('EQUIPMENT_MANAGE_REGISTER_STATUS_PLACEHOLDER')}
              width="100%"
              itemList={optionsMemo.status}
              value={formData.status}
              onChange={formHandler('status')}
            />
          </Box>
          {isPad && (
            <StyleButton
              id="equipment-register-button-scan-status"
              className={classes.scanButton}
              width="14.125rem"
              text={i18n.t('EQUIPMENT_MANAGE_REGISTER_SCAN_STATUS')}
              Icon={ScanIcon}
              iconStyle={{ fontSize: '1.5rem', marginRight: '0.75rem' }}
              onClick={openStatusScanPage}
            />
          )}
        </Box>
        <div
          id="equipment-register-button-otherinformation"
          className={classes.otherInfomation}
          onClick={otherInformationController}
          tabIndex={0}
          onKeyUp={(e) => onKeyUpEnterOrSpace(e, otherInformationController)}
        >
          <div className={classes.dropDownButton}>
            {otherInformationOpen ? (
              <DropUpIcon className={classes.dropDownIcon} />
            ) : (
              <DropDownIcon className={classes.dropDownIcon} />
            )}
          </div>
          {i18n.t('EQUIPMENT_MANAGE_REGISTER_OTHER')}
        </div>
        {otherInformationOpen && (
          <Box display="flex" p="1.5rem 0" flexDirection="column" borderBottom="solid 1px #e6e7e8">
            <Box display="flex" flexDirection="column" width="100%" mb="1.5rem">
              <div className={classes.inputTitle}>{i18n.t('EQUIPMENT_MANAGE_REGISTER_LOCAL_CODE')}</div>
              <InputEquipment
                id="equipment-register-input-local"
                className={classes.inputBase}
                value={formData.localCode}
                onChange={formHandler('localCode')}
                placeholder={i18n.t('EQUIPMENT_MANAGE_REGISTER_LOCAL_CODE_PLACEHOLDER')}
                check={checkLocalCode}
                error={Boolean(errors.localCode)}
                errorMsg={errors.localCode}
              />
            </Box>
            <Box display="flex" justifyContent="space-between" gridColumnGap="1.438rem" mb="1.5rem">
              <Box display="flex" flexDirection="column" width="50%">
                <div className={classes.inputTitle}>{i18n.t('EQUIPMENT_MANAGE_REGISTER_MODEL')}</div>
                <AutoCompleteInput
                  id="equipment-register-autocomplete-model"
                  itemList={optionsMemo.model}
                  value={formData.model}
                  onChange={formHandler('model')}
                  placeholder={i18n.t('EQUIPMENT_MANAGE_REGISTER_MODEL_PLACEHOLDER')}
                  freeSolo={true}
                />
              </Box>
              <Box display="flex" flexDirection="column" width="50%">
                <div className={classes.inputTitle}>{i18n.t('EQUIPMENT_MANAGE_REGISTER_BRAND')}</div>
                <AutoCompleteInput
                  id="equipment-register-autocomplete-brand"
                  itemList={optionsMemo.brand}
                  value={formData.brand}
                  onChange={formHandler('brand')}
                  placeholder={i18n.t('EQUIPMENT_MANAGE_REGISTER_BRAND_PLACEHOLDER')}
                  freeSolo={true}
                />
              </Box>
            </Box>
            {/* <LongLoanReminder value={formData.reminderDuration} onChange={formHandler('reminderDuration')} minNum={0} /> */}
            {/* <Box display="flex" flexDirection="column" width="100%">
              <div className={classes.inputTitle}>{i18n.t('EQUIPMENT_MANAGE_REGISTER_REMARK')}</div>
              <InputEquipment
                id="equipment-register-input-remark"
                className={classes.inputBase}
                value={formData.remarks}
                onChange={formHandler('remarks')}
                placeholder={i18n.t('EQUIPMENT_MANAGE_REGISTER_REMARK_PLACEHOLDER')}
                maxLength={EQUIPMENT_LENGTH.EQUIPMENT_REMARK}
              />
            </Box> */}
            <Box display="flex" justifyContent="space-between" flexDirection="column" mb="1.313rem">
              <Box display="flex" justifyContent="space-between">
                <div className={classes.inputTitle}>{i18n.t('EQUIPMENT_MANAGE_REGISTER_REMARK')}</div>
                {/* <AddRemarks list={activeRemarkList} handleSelect={handleSelectRemark} /> */}
              </Box>
              <Box display="flex" gridColumnGap="1.5rem">
                <TextField
                  id="equipment-register-input-remark"
                  className={classes.textField}
                  placeholder={i18n.t('EQUIPMENT_MANAGE_REGISTER_REMARK_PLACEHOLDER')}
                  multiline={true}
                  fullWidth={true}
                  rows={4}
                  value={formData.remarks}
                  onChange={formHandler('remarks')}
                  inputRef={inputRef}
                  onFocus={() => setBarcodeListener(false)}
                  onBlur={() => setBarcodeListener(true)}
                />
                {isPad && (
                  <StyleButton
                    id="ViewRequest-button-scan-remark"
                    className={classes.scanButton}
                    width="14.125rem"
                    text={i18n.t('EQUIPMENT_VIEWREQUEST_NEW_REQUEST_SCAN_EQUIPMENT')}
                    Icon={ScanIcon}
                    iconStyle={{ fontSize: '1.5rem', marginRight: '0.75rem' }}
                    onClick={openScanPageRemarks}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}
      </DiaLog>
    </>
  );
};

export default RegisterEquipment;
